import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/MarketingHeader";

const SECTIONS = [
  ["scope", "1. Scope"],
  ["collect", "2. Information We Collect"],
  ["use", "3. How We Use Information"],
  ["ai-sharing", "4. AI Processing & Subprocessors"],
  ["sharing", "5. How We Share Information"],
  ["retention", "6. Data Retention"],
  ["security", "7. Security"],
  ["rights", "8. Your Rights & Choices"],
  ["cookies", "9. Cookies & Similar Technologies"],
  ["children", "10. Children's Privacy"],
  ["transfers", "11. International Data Transfers"],
  ["changes", "12. Changes to This Policy"],
  ["contact", "13. Contact Us"],
];

export default function Privacy() {
  return (
    <div className="mkt-page" data-testid="page-privacy">
      <MarketingHeader />

      <section className="mkt-hero">
        <div className="mkt-eyebrow">◢ LEGAL</div>
        <h1 className="mkt-h1">Privacy Policy</h1>
        <p className="legal-updated">Last updated: July 22, 2026</p>
      </section>

      <section className="mkt-body">
        <div className="legal-doc">
          <nav className="legal-toc" aria-label="Table of contents">
            <div className="legal-toc-title">On this page</div>
            <ol>
              {SECTIONS.map(([id, label]) => (
                <li key={id}><a href={`#${id}`}>{label}</a></li>
              ))}
            </ol>
          </nav>

          <p>
            This Privacy Policy explains how GovCo.AI, LLC, a Virginia limited liability company
            ("<strong>GovCo.AI</strong>," "<strong>we</strong>," "<strong>us</strong>") collects, uses,
            and shares information when you use GovCo.AI (the "<strong>Service</strong>"). It's part of,
            and should be read alongside, our <Link to="/terms">Terms of Service</Link>.
          </p>

          <h2 id="scope">1. Scope</h2>
          <p>
            GovCo.AI is a business-to-business platform used by organizations and the individuals they
            authorize to represent them. This Policy covers information we collect through the Service
            itself; it does not cover third-party sites you may reach via links from the Service (such as
            SAM.gov).
          </p>

          <h2 id="collect">2. Information We Collect</h2>
          <p>We collect the following categories of information:</p>
          <ul>
            <li>
              <strong>Account & Organization data:</strong> name, work email, hashed password, role, and
              organization membership; audit records of actions you take (logins, edits, approvals,
              exports).
            </li>
            <li>
              <strong>Company capability data:</strong> the information you or your Org enter into your
              company profile and knowledge base — skills, certifications, NAICS codes, security
              clearance status, and past performance records — used to draft and support your proposals.
            </li>
            <li>
              <strong>Proposal content:</strong> solicitation documents you upload, extracted
              requirements, drafted proposal sections, comments, version history, and exported packages.
            </li>
            <li>
              <strong>Opportunity data:</strong> publicly available solicitation listings we import from
              SAM.gov's Opportunities API on your Org's behalf, associated with the proposals you create
              from them.
            </li>
            <li>
              <strong>Usage & device data:</strong> log data such as IP address, browser type, and
              timestamps of requests, collected automatically to operate and secure the Service.
            </li>
          </ul>
          <p>
            We do not knowingly collect classified information, Top Secret information, or Special
            Access Program / Special Access Required (SAP/SAR) material under any circumstances — see{" "}
            <Link to="/terms">Terms of Service, Section 5</Link>. The Service is designed to process
            Controlled Unclassified Information (CUI) subject to the compliance boundaries in our{" "}
            <Link to="/data-processing-addendum">Data Processing &amp; CUI Addendum</Link>; GovCo.AI does
            not hold FedRAMP authorization, CMMC certification, or a completed NIST SP 800-171 assessment,
            and your Org is solely responsible for determining whether the Service meets its specific
            CUI-handling obligations before uploading such data.
          </p>

          <h2 id="use">3. How We Use Information</h2>
          <p>We use the information above to:</p>
          <ul>
            <li>Provide, maintain, and secure the Service, including authenticating your account and enforcing your Org's plan limits and roles;</li>
            <li>Generate AI-assisted drafts, requirement extraction, and inline editing suggestions from your content;</li>
            <li>Send transactional emails (verification, password resets, join-request approvals, and similar account notices);</li>
            <li>Monitor, debug, and improve the Service's reliability and features;</li>
            <li>Comply with legal obligations and enforce our <Link to="/terms">Terms of Service</Link>.</li>
          </ul>
          <p>
            We do not use Your Content to train third-party AI models, and we do not sell your personal
            information.
          </p>

          <h2 id="ai-sharing">4. AI Processing & Subprocessors</h2>
          <p>
            To generate AI Output, relevant excerpts of your solicitation and proposal content are sent
            to our AI model provider (currently Anthropic) for processing. This is necessary to provide
            the drafting features you use — it is not optional background analytics, and it happens each
            time you invoke an AI feature (shredding, generation, or inline editing). See our{" "}
            <Link to="/data-processing-addendum">Data Processing &amp; CUI Addendum</Link> for our full
            subprocessor list.
          </p>

          <h2 id="sharing">5. How We Share Information</h2>
          <p>We share information only in the following circumstances:</p>
          <ul>
            <li>
              <strong>Within your Org:</strong> your Org's Administrators can see membership, roles,
              audit logs, and (depending on role) proposal content belonging to your Org.
            </li>
            <li>
              <strong>Service providers (subprocessors):</strong> companies that host, process, or
              deliver the Service on our behalf, including our AI model provider (Anthropic), database
              and cloud hosting infrastructure, and transactional email delivery — bound by contractual
              confidentiality and data-protection obligations.
            </li>
            <li>
              <strong>Payment processing:</strong> if you're on a paid plan, a third-party payment
              processor handles billing; we do not store your full payment card details ourselves.
            </li>
            <li>
              <strong>Legal & safety:</strong> where required to comply with law, legal process, or to
              protect the rights, property, or safety of GovCo.AI, our users, or others.
            </li>
            <li>
              <strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of
              assets, subject to this Policy continuing to apply to your information.
            </li>
          </ul>

          <h2 id="retention">6. Data Retention</h2>
          <p>
            We retain your Org's data for as long as your Org maintains an active account, plus a
            reasonable period afterward to allow for account recovery, comply with legal obligations, and
            resolve disputes. When an Org is deleted, we delete or anonymize its data within a commercially
            reasonable period, except where we're required to retain it longer by law.
          </p>

          <h2 id="security">7. Security</h2>
          <p>
            We use industry-standard safeguards — including encryption of data in transit, hashed
            password storage, and role-based access controls scoped to your Org — designed to protect
            your information. No system is completely secure, and we cannot guarantee the absolute
            security of information transmitted to or stored by the Service.
          </p>

          <h2 id="rights">8. Your Rights & Choices</h2>
          <p>
            Depending on where you're located, you may have rights to access, correct, export, or
            request deletion of your personal information. Because GovCo.AI accounts belong to an
            Org, most requests about company or proposal data should go through your Org's
            Administrator, who can manage or export it directly within the Service. You can also contact
            us at <a href="mailto:privacy@govco.ai">privacy@govco.ai</a>, and we will route your request
            appropriately, including to your Org's Administrator where applicable.
          </p>

          <h2 id="cookies">9. Cookies & Similar Technologies</h2>
          <p>
            The Service uses a small number of essential cookies to keep you signed in — specifically, an
            HTTP-only cookie that stores your session's refresh token. We do not use these cookies for
            third-party advertising or cross-site tracking.
          </p>

          <h2 id="children">10. Children's Privacy</h2>
          <p>
            The Service is intended for business use by adults and is not directed at children. We do
            not knowingly collect personal information from anyone under 18.
          </p>

          <h2 id="transfers">11. International Data Transfers</h2>
          <p>
            We and our service providers process information in the United States. Where we transfer
            personal information across borders, we use appropriate safeguards as required by
            applicable law.
          </p>

          <h2 id="changes">12. Changes to This Policy</h2>
          <p>
            We may update this Policy from time to time. If we make material changes, we'll provide
            reasonable notice (such as an email to your account's Administrator or an in-app notice)
            before they take effect.
          </p>

          <h2 id="contact">13. Contact Us</h2>
          <p>
            Questions about this Policy or your data can be sent to{" "}
            <a href="mailto:privacy@govco.ai">privacy@govco.ai</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
